/*
 Copyright (c), CKSource Holding sp. z o.o. All rights reserved.
 */

import Descriptor, { IDescriptorBody } from './../../descriptor';
import { IRevisionObject } from '../revision';

interface IBaseRevision {

	/**
	 * As diffData property contains large size document data so this property is stringified and parsed only on the frontend side.
	 *
	 * Explanation:
	 * This is some kind of optimization for the backend. Usually, we did that the properties that was passed into some Message constructor,
	 * after serialization and deserialization were in the same format. Currently, as the diffData that goes into the message is large
	 * object and we do not need to do any logic operations on that property on the backend side after receiving the Message (we will just
	 * push them to the database), there is no need to do JSON.parse() when deserializing the message and again JSON.stringify() before
	 * pushing them into the database.
	 *
	 * That is why JSON.parse() and JSON.stringify() was not used in toJson() and fromJSON() methods.
	 */
	diffData?: string;
	revisionId: string;
	name?: string;
	creatorId?: string;
	fromVersion?: number;
	toVersion?: number;
	isEmptyCurrent?: boolean;
}

export interface IRevisionJSON extends IBaseRevision {
	createdAt?: string;
	attributes?: string;
	authorsIds?: string;
}

export interface IRevision extends IBaseRevision {
	createdAt?: Date;
	attributes?: Record<string, unknown>;
	authorsIds?: string[];
}

export default class RevisionDescriptor extends Descriptor {
	public static readonly DESCRIPTOR_NAME: string = 'RevisionDescriptor';

	public static readonly DESCRIPTOR: IDescriptorBody = /* #__PURE__ -- @preserve */ ( () => ( {
		fields: {
			revisionId: {
				type: 'string',
				id: 1
			},
			name: {
				type: 'string',
				id: 2
			},
			creatorId: {
				type: 'string',
				id: 3
			},
			authorsIds: {
				type: 'string',
				id: 4
			},
			diffData: {
				type: 'string',
				id: 5
			},
			createdAt: {
				type: 'string',
				id: 6
			},
			attributes: {
				type: 'string',
				id: 7
			},
			toVersion: {
				type: 'uint32',
				id: 8
			},
			fromVersion: {
				type: 'uint32',
				id: 9
			},
			isEmptyCurrent: {
				type: 'bool',
				id: 10
			}
		}
	} ) )();

	public static create( object: IRevisionObject ): IRevision {
		return {
			revisionId: object.id,
			name: object.name,
			creatorId: object.creatorId,
			createdAt: object.createdAt,
			fromVersion: object.fromVersion,
			toVersion: object.toVersion,
			isEmptyCurrent: object.isEmptyCurrent,
			diffData: object.diffData ? JSON.stringify( object.diffData ) : undefined,
			attributes: object.attributes,
			authorsIds: object.authorsIds
		};
	}

	public static toJSON( revision: IRevision ): IRevisionJSON {
		return {
			revisionId: revision.revisionId,
			name: revision.name,
			creatorId: revision.creatorId,
			authorsIds: revision.authorsIds ? JSON.stringify( revision.authorsIds ) : undefined,
			diffData: revision.diffData,
			createdAt: revision.createdAt?.toISOString(),
			attributes: revision.attributes ? JSON.stringify( revision.attributes ) : undefined,
			toVersion: revision.toVersion,
			fromVersion: revision.fromVersion,
			isEmptyCurrent: revision.isEmptyCurrent
		};
	}

	public static fromJSON( json: IRevisionJSON ): IRevision {
		// Copy objects to handle optional values.
		// This is needed because protobuf.js creates getters that return default values on undefined.
		// The same thing is done in the ObjectCompressor.decode method, but not for nested objects.
		json = { ...json };

		return {
			revisionId: json.revisionId,
			name: json.name,
			creatorId: json.creatorId,
			authorsIds: json.authorsIds ? JSON.parse( json.authorsIds ) : undefined,
			diffData: json.diffData,
			createdAt: json.createdAt ? new Date( json.createdAt ) : undefined,
			attributes: json.attributes ? JSON.parse( json.attributes ) : undefined,
			toVersion: json.toVersion,
			fromVersion: json.fromVersion,
			isEmptyCurrent: json.isEmptyCurrent
		};
	}

	public static toObject( revision: IRevision ): IRevisionObject {
		const revisionObject: IRevisionObject = {
			id: revision.revisionId,
			name: revision.name,
			creatorId: revision.creatorId,
			createdAt: revision.createdAt,
			fromVersion: revision.fromVersion,
			toVersion: revision.toVersion,
			isEmptyCurrent: revision.isEmptyCurrent,
			diffData: revision.diffData ? JSON.parse( revision.diffData ) : undefined,
			attributes: revision.attributes,
			authorsIds: revision.authorsIds
		};

		return _deleteUndefinedProperties( revisionObject );
	}
}

/**
 * Removes undefined properties from revision.
 *
 * Revisions should not contain undefined properties because the conflict resolving mechanism implemented by CF team requires it.
 */
function _deleteUndefinedProperties( revisionObject: IRevisionObject ): IRevisionObject {
	for ( const [ key, value ] of Object.entries( revisionObject ) ) {
		if ( value === undefined ) {
			// @ts-ignore Deleting properties with key usage requires adding to IRevisionObject interface "[ key: string ]: unknown" rule,
			// so it is better to ignore this than allow assigning any key to this object.
			delete revisionObject[ key ];
		}
	}

	return revisionObject;
}
